import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import renderMathInElement from "katex/contrib/auto-render";
import "katex/dist/katex.min.css";

const BOT_NAME = "AIdos";

const HEADER_BG = "#6B8FBA";
const SEND_BG = "#3777C4";

function formatTime(date = new Date()) {
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
}

function ChatIcon({ size = 28, className = "" }) {
  return (
    <img
      src="/chat_icon.png"
      alt="chat"
      width={size}
      height={size}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function SendIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.5 11.13L3.5 3.5C3 3.28 2.5 3.75 2.7 4.28L5.4 11.5L2.7 18.72C2.5 19.25 3 19.72 3.5 19.5L21.5 11.87C21.95 11.67 21.95 11.33 21.5 11.13Z"
        stroke="white"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M5.4 11.5H12"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M6 6L18 18M18 6L6 18"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ExpandIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M9 4H4V9M15 4H20V9M9 20H4V15M15 20H20V15"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MinimizeIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5 13H19" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function BotAvatar({ size = 44, ring = false }) {
  return (
    <div
      className={`flex-shrink-0 rounded-full bg-white flex items-center justify-center overflow-hidden ${
        ring ? "ring-1 ring-[#cfd9e6]" : ""
      }`}
      style={{ width: size, height: size }}
    >
      <ChatIcon size={Math.round(size * 0.78)} />
    </div>
  );
}

const MATH_DELIMITERS = [
  { left: "$$", right: "$$", display: true },
  { left: "\\[", right: "\\]", display: true },
  { left: "\\(", right: "\\)", display: false },
  { left: "\\begin{equation}", right: "\\end{equation}", display: true },
  { left: "\\begin{align}", right: "\\end{align}", display: true },
  { left: "\\begin{alignat}", right: "\\end{alignat}", display: true },
  { left: "\\begin{gather}", right: "\\end{gather}", display: true },
  { left: "$", right: "$", display: false },
];

function LatexText({ text }) {
  const hostRef = useRef(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    host.textContent = text || "";
    try {
      renderMathInElement(host, {
        delimiters: MATH_DELIMITERS,
        strict: false,
        // throwOnError оставляем включённым: на ошибке auto-render
        // возвращает исходный текст вместо красной подсветки
        errorCallback: () => {},
      });
    } catch (_) {
      host.textContent = text || "";
    }
  }, [text]);

  return (
    <span
      ref={hostRef}
      className="ai-chat-message whitespace-pre-wrap break-words"
    />
  );
}

const LANG_MAP = { ru: "ru", kz: "kk", en: "en" };

function buildWsUrl(token) {
  const apiUrl = import.meta.env.VITE_OQULYQTAR_API_URL || "";
  const wsBase = apiUrl.replace(/^http/i, "ws");
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${wsBase}/ai/chat/ws${tokenParam}`;
}

function AIChatWidget() {
  const token = useSelector((s) => s.auth?.token);
  const { t, i18n } = useTranslation();

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [input, setInput] = useState("");
  const [connState, setConnState] = useState("idle");
  const [isStreaming, setIsStreaming] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "greet",
      role: "bot",
      i18nKey: "aiChat.greeting",
      text: t("aiChat.greeting"),
      time: "10:30",
    },
  ]);

  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const sessionIdRef = useRef(null);
  const pendingBotIdRef = useRef(null);
  const pendingFirstTurnRef = useRef(null);
  const sessionReadyRef = useRef(null);

  useEffect(() => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.i18nKey) return { ...m, text: t(m.i18nKey) };
        return m;
      }),
    );
  }, [t, i18n.language]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, expanded]);

  const updateMessage = useCallback((id, updater) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...updater(m) } : m)),
    );
  }, []);

  const failPendingBot = useCallback(
    (errorText) => {
      const id = pendingBotIdRef.current;
      if (!id) return;
      const i18nKey = errorText ? undefined : "aiChat.connectionError";
      updateMessage(id, (m) => ({
        text: m.text || errorText || t(i18nKey),
        i18nKey: m.text || errorText ? undefined : i18nKey,
        streaming: false,
        error: !m.text,
      }));
      pendingBotIdRef.current = null;
      setIsStreaming(false);
    },
    [t, updateMessage],
  );

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      if (ws.readyState === WebSocket.OPEN && sessionIdRef.current) {
        ws.send(
          JSON.stringify({ action: "end", sessionId: sessionIdRef.current }),
        );
      }
      ws.close();
    } catch (_) {}
    wsRef.current = null;
    sessionIdRef.current = null;
    sessionReadyRef.current = null;
    setConnState("idle");
  }, []);

  const ensureConnection = useCallback(() => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING) &&
      sessionReadyRef.current
    ) {
      return sessionReadyRef.current;
    }

    setConnState("connecting");
    const url = buildWsUrl(token);
    console.log("[AIChat] connecting", url);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    const ready = new Promise((resolve, reject) => {
      ws.addEventListener("open", () => {
        const language = LANG_MAP[i18n.language] || "ru";
        console.log("[AIChat] open, sending start", { language });
        ws.send(JSON.stringify({ action: "start", language }));
      });

      ws.addEventListener("message", (event) => {
        console.log("[AIChat] message", event.data);
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (_) {
          return;
        }
        switch (data.kind) {
          case "session": {
            sessionIdRef.current = data.session?.sessionId || null;
            setConnState("open");
            resolve(sessionIdRef.current);
            break;
          }
          case "ack":
            break;
          case "sources":
            break;
          case "token": {
            const id = pendingBotIdRef.current;
            const piece = data.token?.text || "";
            if (!id || !piece) break;
            updateMessage(id, (m) => ({
              text: (m.text || "") + piece,
              streaming: true,
            }));
            break;
          }
          case "complete": {
            const id = pendingBotIdRef.current;
            const finalText = data.complete?.answerText;
            if (id) {
              updateMessage(id, (m) => ({
                text:
                  typeof finalText === "string" && finalText.length
                    ? finalText
                    : m.text,
                streaming: false,
                time: formatTime(),
              }));
            }
            pendingBotIdRef.current = null;
            setIsStreaming(false);
            break;
          }
          case "error": {
            failPendingBot(data.error?.message);
            break;
          }
          default:
            break;
        }
      });

      ws.addEventListener("error", (e) => {
        console.error("[AIChat] error", e);
        setConnState("error");
        reject(new Error("ws error"));
      });

      ws.addEventListener("close", (e) => {
        console.warn("[AIChat] close", {
          code: e.code,
          reason: e.reason,
          wasClean: e.wasClean,
        });
        wsRef.current = null;
        sessionIdRef.current = null;
        sessionReadyRef.current = null;
        setConnState("idle");
        failPendingBot();
      });
    });

    sessionReadyRef.current = ready;
    return ready;
  }, [token, i18n.language, updateMessage, failPendingBot]);

  useEffect(() => () => closeSocket(), [closeSocket]);

  const handleOpen = () => {
    setOpen(true);
    setHasUnread(false);
  };

  const handleClose = () => {
    setOpen(false);
    setExpanded(false);
    closeSocket();
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (!token) {
      setMessages((prev) => [
        ...prev,
        {
          id: `u-${Date.now()}`,
          role: "user",
          text: trimmed,
          time: formatTime(),
        },
        {
          id: `b-${Date.now() + 1}`,
          role: "bot",
          i18nKey: "aiChat.loginRequired",
          text: t("aiChat.loginRequired"),
          time: formatTime(),
          streaming: false,
        },
      ]);
      setInput("");
      return;
    }

    const userId = `u-${Date.now()}`;
    const botId = `b-${Date.now() + 1}`;
    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", text: trimmed, time: formatTime() },
      { id: botId, role: "bot", text: "", time: "", streaming: true },
    ]);
    pendingBotIdRef.current = botId;
    setIsStreaming(true);
    setInput("");

    try {
      const sessionId = await ensureConnection();
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !sessionId) {
        throw new Error("not connected");
      }
      ws.send(
        JSON.stringify({
          action: "turn",
          sessionId,
          text: trimmed,
        }),
      );
    } catch (_) {
      failPendingBot();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isSending = isStreaming || connState === "connecting";

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t("aiChat.openLabel")}
        className="fixed bottom-5 right-5 z-[10000] group"
        style={{ width: 64, height: 64 }}
      >
        <span className="relative block w-full h-full rounded-full bg-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] border border-[#1A60B3] transition-transform duration-150 group-hover:scale-105 group-active:scale-95">
          <span className="absolute inset-0 flex items-center justify-center">
            <ChatIcon size={42} />
          </span>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full bg-[#E84C3D] text-white text-xs font-semibold flex items-center justify-center shadow-md">
              1
            </span>
          )}
        </span>
      </button>
    );
  }

  const widthClass = expanded
    ? "w-[min(760px,calc(100vw-32px))]"
    : "w-[min(400px,calc(100vw-32px))]";
  const heightClass = expanded
    ? "h-[min(640px,calc(100vh-48px))]"
    : "h-[min(560px,calc(100vh-48px))]";

  return (
    <div
      className={`fixed bottom-5 right-5 z-[10000] ${widthClass} ${heightClass} flex flex-col rounded-2xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.2)] overflow-hidden`}
      role="dialog"
      aria-label={t("aiChat.dialogLabel")}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 text-white"
        style={{ background: HEADER_BG }}
      >
        <BotAvatar size={44} />
        <div className="flex-1 min-w-0">
          <div className="text-[17px] font-semibold leading-tight truncate">
            {BOT_NAME}
          </div>
          <div className="text-[13px] opacity-90 leading-tight truncate">
            {t("aiChat.subtitle")}
          </div>
        </div>

        {expanded ? (
          <>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={t("aiChat.minimizeLabel")}
              className="p-1.5 rounded hover:bg-white/15 transition-colors"
            >
              <MinimizeIcon />
            </button>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("aiChat.closeLabel")}
              className="p-1.5 rounded hover:bg-white/15 transition-colors"
            >
              <CloseIcon />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-label={t("aiChat.expandLabel")}
              className="p-1.5 rounded hover:bg-white/15 transition-colors hidden tablet:inline-flex"
            >
              <ExpandIcon />
            </button>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("aiChat.closeLabel")}
              className="p-1.5 rounded hover:bg-white/15 transition-colors"
            >
              <CloseIcon />
            </button>
          </>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 bg-white"
        style={{ scrollbarWidth: "thin" }}
      >
        {messages.map((m) => {
          if (m.role === "bot") {
            const isEmptyStreaming = m.streaming && !m.text;
            return (
              <div key={m.id} className="mb-3">
                <div className="flex items-start gap-2">
                  <BotAvatar size={28} ring />
                  <div
                    className={`max-w-[78%] bg-[#F2F3F5] ${
                      m.error ? "text-[#b94a48]" : "text-[#222]"
                    } text-[15px] leading-snug px-4 py-2.5 rounded-2xl rounded-tl-md`}
                  >
                    {isEmptyStreaming ? (
                      <span className="inline-flex items-center gap-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-[#9aa3ad] animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-[#9aa3ad] animate-bounce"
                          style={{ animationDelay: "120ms" }}
                        />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-[#9aa3ad] animate-bounce"
                          style={{ animationDelay: "240ms" }}
                        />
                      </span>
                    ) : (
                      <LatexText text={m.text} />
                    )}
                  </div>
                </div>
                {m.time && (
                  <div className="ml-10 mt-1 text-[12px] text-[#9aa3ad]">
                    {m.time}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div key={m.id} className="mb-3 flex flex-col items-end">
              <div
                className="max-w-[78%] text-white text-[15px] leading-snug px-4 py-2.5 rounded-2xl rounded-tr-md"
                style={{ background: SEND_BG }}
              >
                <LatexText text={m.text} />
              </div>
              {m.time && (
                <div className="mr-1 mt-1 text-[12px] text-[#9aa3ad]">
                  {m.time}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="px-3 py-3 border-t border-[#eef0f3] bg-white flex items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("aiChat.inputPlaceholder")}
          disabled={isSending}
          className="flex-1 h-11 px-4 rounded-full bg-[#F6F7F9] border border-[#e6e8ec] text-[15px] text-[#222] placeholder-[#9aa3ad] outline-none focus:border-[#b9c4d4] disabled:opacity-70"
        />
        <button
          type="button"
          onClick={handleSend}
          aria-label={t("aiChat.sendLabel")}
          className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-50"
          style={{ background: SEND_BG }}
          disabled={!input.trim() || isSending}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

export default memo(AIChatWidget);
